import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Modal,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { ReplaceData } from 'services/CommonServices';
import { SALES_ENDPOINT } from 'utils/Constant';

interface ShortlistPopupProps {
  open: boolean;
  onClose: () => void;
  onProceed?: (selectedItems: string[]) => void;
  candidateName?: string;
  jobTitle?: string;
  attachmentsCount?: number;
  candidateInternalId?: string;
}

const modalStyle = {
  bgcolor: 'background.paper',
  borderRadius: '8px',
  boxShadow: 24,
  left: '50%',
  maxWidth: '90%',
  outline: 'none',
  position: 'absolute' as const,
  top: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
};

export const ShortlistPopup: React.FC<ShortlistPopupProps> = ({
  open,
  onClose,
  onProceed,
  candidateName,
  jobTitle,
  attachmentsCount = 4,
  candidateInternalId,
}) => {
  const parms = useParams();
  const [selectedItems, setSelectedItems] = useState<string[]>([
    'resume',
    'overview',
  ]);

  const handleCheckboxChange = (item: string) => {
    setSelectedItems((prev) =>
      prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item],
    );
  };

  const handleProceed = () => {
    if (onProceed) {
      onProceed(selectedItems);
    }
    if (onProceed) {
      ReplaceData(
        parms.jobId || '',
        { candidateId: [candidateInternalId || ''] },
        SALES_ENDPOINT.SHORTLIST,
        true,
      )
        .then((resp: any) => {
          onClose();
        })
        .catch((err: any) => {});
    }
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="select-items-modal-title"
      aria-describedby="select-items-modal-description">
      <Box sx={modalStyle}>
        {/* Close Button */}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            '&:hover': {
              backgroundColor: '#f3f4f6',
            },
            color: '#6b7280',
            position: 'absolute',
            right: 8,
            top: 8,
          }}>
          <CloseIcon />
        </IconButton>

        {/* Title */}
        <Typography
          id="select-items-modal-title"
          sx={{
            color: '#111827',
            fontFamily: 'Red Hat Display SemiBold',
            fontSize: '18px',
            fontWeight: 600,
            marginBottom: '8px',
            paddingTop: '24px',
            paddingX: '24px',
          }}>
          Select Items to Include
        </Typography>

        {/* Subtitle */}
        <Typography
          sx={{
            color: '#6b7280',
            fontFamily: 'Red Hat Display Regular',
            fontSize: '14px',
            marginBottom: '24px',
            paddingX: '24px',
          }}>
          These are the final documents being submitted.
        </Typography>

        {/* Checkbox List */}
        <Box
          sx={{
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            flexDirection: 'column',
            paddingBottom: '24px',
            paddingX: '24px',
          }}>
          {/* Resume */}
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedItems.includes('resume')}
                onChange={() => handleCheckboxChange('resume')}
                sx={{
                  '&.Mui-checked': {
                    color: '#111827',
                  },
                }}
              />
            }
            label={
              <Typography
                sx={{
                  color: '#111827',
                  fontFamily: 'Red Hat Display Regular',
                  fontSize: '14px',
                }}>
                Resume
              </Typography>
            }
            sx={{
              marginBottom: '16px',
              marginLeft: 0,
            }}
          />

          {/* Candidate Overview Summary */}
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedItems.includes('overview')}
                onChange={() => handleCheckboxChange('overview')}
                sx={{
                  '&.Mui-checked': {
                    color: '#111827',
                  },
                }}
              />
            }
            label={
              <Typography
                sx={{
                  color: '#111827',
                  fontFamily: 'Red Hat Display Regular',
                  fontSize: '14px',
                }}>
                Candidate Overview Summary
              </Typography>
            }
            sx={{
              marginBottom: '16px',
              marginLeft: 0,
            }}
          />

          {/* All Attachments */}
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedItems.includes('attachments')}
                onChange={() => handleCheckboxChange('attachments')}
                sx={{
                  '&.Mui-checked': {
                    color: '#111827',
                  },
                }}
              />
            }
            label={
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                }}>
                <Typography
                  sx={{
                    color: '#111827',
                    fontFamily: 'Red Hat Display Regular',
                    fontSize: '14px',
                  }}>
                  All Attachments ({attachmentsCount} files)
                </Typography>
                <ExpandMoreIcon
                  sx={{
                    color: '#6b7280',
                    fontSize: '20px',
                  }}
                />
              </Box>
            }
            sx={{
              marginLeft: 0,
              width: '100%',
            }}
          />
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end',
            padding: '16px 24px',
          }}>
          <Button
            onClick={handleCancel}
            sx={{
              '&:hover': {
                backgroundColor: '#f9fafb',
              },
              backgroundColor: '#ffffff',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              color: '#111827',
              fontFamily: 'Red Hat Display Medium',
              fontSize: '14px',
              fontWeight: 500,
              padding: '8px 16px',
              textTransform: 'none',
            }}>
            Cancel
          </Button>
          <Button
            onClick={handleProceed}
            sx={{
              '&:hover': {
                backgroundColor: '#111827',
              },
              backgroundColor: '#111827',
              borderRadius: '6px',
              color: '#ffffff',
              fontFamily: 'Red Hat Display Medium',
              fontSize: '14px',
              fontWeight: 500,
              padding: '8px 16px',
              textTransform: 'none',
            }}>
            Proceed
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
